/**
 * SSE-driven live progress for a single run.
 *
 * Opens an EventSource against /api/novels/:id/events. Progress
 * events are JSON-encoded and pushed into the React Query cache for
 * the run — components using `useQuery(['run', id])` re-render.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { openRunEvents } from "./client";
import type { ProgressEvent, RunRow } from "./client";

export function useRunEvents(runId: string | undefined) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!runId) return;
    const es = openRunEvents(runId);

    const onProgress = (e: MessageEvent) => {
      try {
        const event: ProgressEvent = JSON.parse(e.data);
        const kind = event.kind ?? event.type ?? "";
        
        qc.setQueryData<{ run: RunRow; bible?: unknown } | undefined>(
          ["run", runId],
          (prev) => {
            if (!prev) return prev;
            const patch: Partial<RunRow> = {};
            if (typeof event.chapterNo === "number" && event.chapterNo > 0) {
              patch.currentChapter = event.chapterNo;
            }
            if (kind === "checkpoint") {
              patch.status = "paused";
            } else if (kind === "error") {
              patch.status = "failed";
              patch.errorMessage = event.message ?? "Unknown error";
            } else if (kind === "done") {
              patch.status = "completed";
            }
            return { ...prev, run: { ...prev.run, ...patch } };
          },
        );

        if (kind === "chapter_done" || kind === "done" || kind === "bible_ready") {
          qc.invalidateQueries({ queryKey: ["run", runId] });
          qc.invalidateQueries({ queryKey: ["chapters", runId] });
        }
      } catch {
        // ignore malformed events
      }
    };

    es.addEventListener("progress", onProgress as EventListener);
    es.addEventListener("ping", () => {
      /* keepalive — nothing to do */
    });
    es.onerror = () => {
      // EventSource will auto-reconnect. We don't tear it down here.
    };

    return () => {
      es.removeEventListener("progress", onProgress as EventListener);
      es.close();
    };
  }, [runId, qc]);
}
